import { EventEmitter } from "eventemitter3";
import { MediaDevicesEvents, PermissionStatus, MediaDevices, NO_DEVICE_ID } from "./media-devices-utils";
import { isIOS as detectIOS } from "./is-mobile";
import {
  ROOM_RESOURCE_STATE_CHANGED,
  canAcquireRoomResource,
  getRoomResource,
  RoomResourceIds
} from "./room-resource-state-utils";
import { unavailableRoomResourceReason } from "./room-resource-ui-utils";
import {
  createCleanMicStream,
  preloadMicNoiseSuppressor as preloadMicNoiseSuppressorAssets
} from "./mic-audio-processing";

const isMobile = AFRAME.utils.device.isMobile();
const isIOS = detectIOS();

// This is a list of regexes that match the microphone labels of HMDs.
//
// If entering VR mode, and if any of these regexes match an audio device,
// the user will be prevented from entering VR until one of those devices is
// selected as the microphone.
//
// Note that this doesn't have to be exhaustive: if no devices match any regex
// then we rely upon the user to select the proper mic.
const HMD_MIC_REGEXES = [/\Wvive\W/i, /\Wrift\W/i];
const MIC_PROCESSING_PREFERENCE_KEYS = ["disableEchoCancellation", "disableNoiseSuppression", "disableAutoGainControl"];

function micAudioProcessingConfigKey(audioConstraints = {}, preferences = {}) {
  const micProcessingPreferences = MIC_PROCESSING_PREFERENCE_KEYS.reduce((result, key) => {
    result[key] = preferences[key] === true;
    return result;
  }, {});

  return JSON.stringify({
    audioConstraints: audioConstraints || {},
    micProcessingPreferences
  });
}

function hasLiveAudioTrack(stream) {
  return !!stream?.getAudioTracks?.().some(track => track && track.readyState !== "ended" && track.stopped !== true);
}

function liveAudioTrackFromStream(stream) {
  return (
    stream?.getAudioTracks?.().find(track => track && track.readyState !== "ended" && track.stopped !== true) || null
  );
}

function isLiveAudioTrack(track) {
  return !!track && track.readyState !== "ended" && track.stopped !== true;
}

export default class MediaDevicesManager extends EventEmitter {
  constructor(scene, store, audioSystem) {
    super();

    this._scene = scene;
    this._store = store;
    this._micDevices = [];
    this._videoDevices = [];
    this._videoActiveId = NO_DEVICE_ID;
    this._outputDevices = [];
    this._deviceId = null;
    this._audioTrack = null;
    this._micAudioProcessing = null;
    this._micAudioProcessingConfigKey = null;
    this._micAudioProcessingAttached = false;
    this._micAudioProcessingLabel = null;
    this._micRequestedDeviceId = null;
    this._micNoiseSuppressorPreload = null;
    this._pendingMutedMicPrewarm = null;
    this._micTrackEndedTarget = null;
    this._micTrackEndedListener = null;
    this._videoShareStreams = new Set();
    this.audioSystem = audioSystem;
    this._mediaStream = audioSystem.outboundStream;
    this._permissionsStatus = {
      [MediaDevices.MICROPHONE]: PermissionStatus.PROMPT,
      [MediaDevices.SPEAKERS]: PermissionStatus.PROMPT,
      [MediaDevices.CAMERA]: PermissionStatus.PROMPT,
      [MediaDevices.SCREEN]: PermissionStatus.PROMPT
    };
    this._hasGenericMicPermission = false;
    this._pendingMicEntryEnable = null;

    this.onDeviceChange = this.onDeviceChange.bind(this);
    navigator.mediaDevices.addEventListener("devicechange", this.onDeviceChange);
    this.onPermissionsUpdated = this.onPermissionsUpdated.bind(this);
    APP.hubChannel.addEventListener("permissions_updated", this.onPermissionsUpdated);
  }

  static get isAudioOutputSelectEnabled() {
    return true;
  }

  static get isAudioInputSelectEnabled() {
    return true;
  }

  get deviceId() {
    return this._deviceId;
  }

  set deviceId(deviceId) {
    this._deviceId = deviceId;
  }

  get audioTrack() {
    return this._audioTrack;
  }

  set audioTrack(audioTrack) {
    this._audioTrack = audioTrack;
  }

  get micAudioProcessingLabel() {
    return this._micAudioProcessingLabel;
  }

  get defaultInputDeviceId() {
    return (
      this._micDevices.find(device => device.value === "default")?.value || this._micDevices[0]?.value || NO_DEVICE_ID
    );
  }

  get defaultOutputDeviceId() {
    return this._outputDevices.length > 0 ? "default" : NO_DEVICE_ID;
  }

  get defaultVideoDeviceId() {
    return this._videoDevices.length > 0 ? this._videoDevices[0].value : NO_DEVICE_ID;
  }

  get micDevicesOptions() {
    return this._micDevices.length > 0 ? this._micDevices : [{ value: NO_DEVICE_ID, label: "None" }];
  }

  get videoDevicesOptions() {
    return this._videoDevices.length > 0 ? this._videoDevices : [{ value: NO_DEVICE_ID, label: "-" }];
  }

  get outputDevicesOptions() {
    return this._outputDevices.length > 0 ? this._outputDevices : [{ value: NO_DEVICE_ID, label: "None" }];
  }

  get mediaStream() {
    return this._mediaStream;
  }

  set mediaStream(mediaStream) {
    this._mediaStream = mediaStream;
  }

  get selectedMicLabel() {
    return this.micLabelForAudioTrack(this.audioTrack);
  }

  get selectedMicDeviceId() {
    if (
      MediaDevicesManager.isAudioInputSelectEnabled &&
      this._permissionsStatus[MediaDevices.MICROPHONE] !== PermissionStatus.GRANTED
    )
      return NO_DEVICE_ID;
    if (!this.audioTrack) return this.preferredOrDefaultMicDeviceId();
    if (this._micRequestedDeviceId === "default") return "default";
    return this.audioTrack.getSettings?.().deviceId || this.deviceIdForMicDeviceLabel(this.selectedMicLabel);
  }

  get currentMicCapture() {
    const rawStream = this._micAudioProcessing?.rawStream || null;
    const cleanStream = this._micAudioProcessing?.cleanStream || null;

    return {
      deviceId: this.audioTrack?.getSettings?.().deviceId || this.selectedMicDeviceId,
      rawStream,
      rawTrack: this.audioTrack,
      cleanStream,
      cleanTrack: liveAudioTrackFromStream(cleanStream),
      processing: this.micAudioProcessingLabel
    };
  }

  getPreferredMicCaptureTrack({ preferClean = true } = {}) {
    const capture = this.currentMicCapture;

    if (preferClean && isLiveAudioTrack(capture.cleanTrack)) {
      return capture.cleanTrack;
    }

    if (isLiveAudioTrack(capture.rawTrack)) {
      return capture.rawTrack;
    }

    if (!preferClean && isLiveAudioTrack(capture.cleanTrack)) {
      return capture.cleanTrack;
    }

    return null;
  }

  getPreferredMicCaptureStream({ preferClean = true, MediaStreamConstructor } = {}) {
    const capture = this.currentMicCapture;

    if (preferClean && hasLiveAudioTrack(capture.cleanStream)) {
      return capture.cleanStream;
    }

    if (!preferClean && hasLiveAudioTrack(capture.rawStream)) {
      return capture.rawStream;
    }

    const track = this.getPreferredMicCaptureTrack({ preferClean });
    if (!track) {
      return null;
    }

    if (capture.rawStream?.getAudioTracks?.().includes(track)) {
      return capture.rawStream;
    }

    if (capture.cleanStream?.getAudioTracks?.().includes(track)) {
      return capture.cleanStream;
    }

    const StreamConstructor = MediaStreamConstructor || (typeof MediaStream !== "undefined" ? MediaStream : undefined);

    return StreamConstructor ? new StreamConstructor([track]) : null;
  }

  get selectedSpeakersDeviceId() {
    const { preferredSpeakers } = this._store.state.preferences;
    const exists = this._outputDevices.some(device => {
      return device.value === preferredSpeakers;
    });
    return exists ? preferredSpeakers : this.defaultOutputDeviceId;
  }

  get selectedVideoDeviceId() {
    return this._videoActiveId;
  }

  setDeviceId(deviceId) {
    this._videoActiveId = deviceId;
  }

  get selectedVideoDeviceConstraint() {
    if (!this._videoActiveId || this._videoActiveId === NO_DEVICE_ID) return undefined;

    const selectedDeviceAvailable = this._videoDevices.some(device => device.value === this._videoActiveId);
    if (this._videoDevices.length > 0 && !selectedDeviceAvailable) return undefined;

    return { exact: this._videoActiveId };
  }

  hasVideoDevice(deviceId) {
    return !!deviceId && deviceId !== NO_DEVICE_ID && this._videoDevices.some(device => device.value === deviceId);
  }

  selectPreferredVideoDeviceIfAvailable() {
    if (this.hasVideoDevice(this._videoActiveId)) return;

    const { preferredCamera } = this._store.state.preferences;

    if (this.hasVideoDevice(preferredCamera)) {
      this.setDeviceId(preferredCamera);
    }
  }

  hasMicDevice(deviceId) {
    return !!deviceId && deviceId !== NO_DEVICE_ID && this._micDevices.some(device => device.value === deviceId);
  }

  preferredOrDefaultMicDeviceId() {
    const { preferredMic } = this._store.state.preferences;

    if (this.hasMicDevice(preferredMic)) {
      return preferredMic;
    }

    return this.defaultInputDeviceId;
  }

  updatePreferredVideoDevice(deviceId) {
    this._store.update({
      preferences: {
        preferredCamera: deviceId || NO_DEVICE_ID
      }
    });
  }

  get selectedCameraVideoConstraints() {
    const videoConstraints = {
      width: isIOS ? { max: 1280 } : { max: 1280, ideal: 720 },
      frameRate: 30
    };
    const deviceId = this.selectedVideoDeviceConstraint;

    if (deviceId) {
      videoConstraints.deviceId = deviceId;
    }

    return videoConstraints;
  }

  get isMicShared() {
    return this.audioTrack !== null && this.getPermissionsStatus(MediaDevices.MICROPHONE) === PermissionStatus.GRANTED;
  }

  get isMicPreparedLocalOnly() {
    return this.isMicShared && !this._micAudioProcessingAttached;
  }

  get isVideoShared() {
    return this._mediaStream?.getVideoTracks().length > 0;
  }

  get isWebcamShared() {
    return !!this._mediaStream?.getVideoTracks().some(track => track["_hubs_contentHint"] === MediaDevices.CAMERA);
  }

  get isScreenShared() {
    return !!this._mediaStream?.getVideoTracks().some(track => track["_hubs_contentHint"] === MediaDevices.SCREEN);
  }

  set micEnabled(enabled) {
    if (!enabled) {
      this.cancelPendingMicEntryEnable();
    }

    if (enabled && !this.canStartMicShare({ notify: true })) {
      return;
    }

    if (enabled && this.isMicPreparedLocalOnly) {
      this.startMicShare({ unmute: true }).catch(error => {
        console.error("Error publishing prepared microphone: ", error);
      });
      return;
    }

    APP.dialog.enableMicrophone(enabled);
  }

  get isMicEnabled() {
    return APP.dialog.isMicEnabled;
  }

  toggleMic() {
    const willEnableMic = !(APP.dialog?.isMicDesiredEnabled ?? this.isMicEnabled);

    if (willEnableMic && !this.canStartMicShare({ notify: true })) {
      return false;
    }

    if (willEnableMic && this.isMicPreparedLocalOnly) {
      this.startMicShare({ unmute: true }).catch(error => {
        console.error("Error publishing prepared microphone: ", error);
      });
      return true;
    }

    APP.dialog.toggleMicrophone();
    return true;
  }

  getRoomResource(resourceId) {
    return APP.dialog?.getRoomResource?.(resourceId) || getRoomResource(APP.dialog?.roomResourceState, resourceId);
  }

  unavailableRoomResourceReason(resourceId, label, { locallyOwned = false } = {}) {
    if (locallyOwned || canAcquireRoomResource(APP.dialog, resourceId)) {
      return null;
    }

    return unavailableRoomResourceReason({
      label,
      resource: this.getRoomResource(resourceId),
      locallyActive: locallyOwned,
      fallbackReason: `${label} is unavailable`
    });
  }

  notifyUnavailableRoomResource(reason) {
    if (!reason) {
      return;
    }

    window.notificationsCenter?.info?.(reason, null, { timeout: 3000 });
  }

  isWaitingForRoomResourceState(resourceId) {
    return APP.dialog?.hasRoomResourceState === false && APP.dialog?.hasRoomResourceLimit?.(resourceId);
  }

  cancelPendingMicEntryEnable() {
    if (!this._pendingMicEntryEnable) {
      return;
    }

    const { dialog, listener } = this._pendingMicEntryEnable;
    dialog?.off?.(ROOM_RESOURCE_STATE_CHANGED, listener);
    this._pendingMicEntryEnable = null;
  }

  tryEnableMicOnEntry({ notify = false } = {}) {
    if (this.isWaitingForRoomResourceState(RoomResourceIds.MICROPHONE)) {
      return false;
    }

    if (!this.canStartMicShare({ notify })) {
      return false;
    }

    this.micEnabled = true;
    return true;
  }

  enableMicOnEntry({ notify = false } = {}) {
    this.cancelPendingMicEntryEnable();

    if (this.tryEnableMicOnEntry({ notify })) {
      return true;
    }

    if (!this.isWaitingForRoomResourceState(RoomResourceIds.MICROPHONE)) {
      return false;
    }

    const dialog = APP.dialog;
    const listener = () => {
      this.cancelPendingMicEntryEnable();
      this.tryEnableMicOnEntry({ notify });
    };

    this._pendingMicEntryEnable = { dialog, listener };
    dialog?.on?.(ROOM_RESOURCE_STATE_CHANGED, listener);
    return false;
  }

  canStartMicShare({ notify = false } = {}) {
    const unavailableReason = this.unavailableRoomResourceReason(RoomResourceIds.MICROPHONE, "Microphone", {
      locallyOwned: this.isMicEnabled
    });

    if (unavailableReason) {
      if (notify) {
        this.notifyUnavailableRoomResource(unavailableReason);
      }

      return false;
    }

    return true;
  }

  canStartVideoShare({ isDisplayMedia, notify = false } = {}) {
    const unavailableReason = this.videoShareUnavailableReason({ isDisplayMedia });

    if (unavailableReason) {
      if (notify) {
        this.notifyUnavailableRoomResource(unavailableReason);
      }

      return false;
    }

    return true;
  }

  videoShareUnavailableReason({ isDisplayMedia } = {}) {
    return this.unavailableRoomResourceReason(
      isDisplayMedia ? RoomResourceIds.SCREEN_SHARE : RoomResourceIds.CAMERA,
      isDisplayMedia ? "Screen share" : "Camera"
    );
  }

  getPermissionsStatus(type) {
    return this._permissionsStatus[type];
  }

  onPermissionsUpdated = () => {
    if (!APP.hubChannel.can("voice_chat")) {
      APP.dialog.enableMicrophone(false);
    }
  };

  async ensureMicPermissionAndEnumerate() {
    if (this._hasGenericMicPermission) {
      return this.fetchMediaDevices();
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this._hasGenericMicPermission = true;

      stream.getTracks().forEach(track => track.stop());

      await this.fetchMediaDevices();

      this._permissionsStatus[MediaDevices.MICROPHONE] = PermissionStatus.GRANTED;
      this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
        mediaDevice: MediaDevices.MICROPHONE,
        status: PermissionStatus.GRANTED
      });
    } catch (err) {
      console.error("Error requesting generic microphone permission:", err);
      this._hasGenericMicPermission = false;

      // Only set DENIED status for actual permission denials (NotAllowedError)
      // Other errors (e.g., NotFoundError, OverconstrainedError) should not block future attempts
      const isPermissionDenied = err.name === "NotAllowedError" || err.name === "PermissionDeniedError";

      if (isPermissionDenied) {
        this._permissionsStatus[MediaDevices.MICROPHONE] = PermissionStatus.DENIED;
        this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
          mediaDevice: MediaDevices.MICROPHONE,
          status: PermissionStatus.DENIED
        });
      } else {
        // For non-permission errors, keep status as PROMPT to allow retries
        console.warn("Microphone access failed but not due to permission denial. Keeping status as PROMPT.");
      }

      await this.fetchMediaDevices();
    }
  }

  onDeviceChange = () => {
    this.fetchMediaDevices().then(() => {
      const pendingOutputAvailable = this._outputDevices.some(
        device => device.value === this._pendingAudioOutputDeviceId
      );
      if (!pendingOutputAvailable) this.changeAudioOutput(this.selectedSpeakersDeviceId);
      this.emit(MediaDevicesEvents.DEVICE_CHANGE, null);
    });
  };

  updatePermissions() {
    const micStatus = this._micDevices.length === 0 ? PermissionStatus.PROMPT : PermissionStatus.GRANTED;
    this._permissionsStatus[MediaDevices.MICROPHONE] = micStatus;
    if (micStatus === PermissionStatus.GRANTED) {
      this._hasGenericMicPermission = true;
      this.preloadMicNoiseSuppressor();
    }
    this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
      mediaDevice: MediaDevices.MICROPHONE,
      status: micStatus
    });
    // const videoStatus = this._videoDevices.length === 0 ? PermissionStatus.PROMPT : PermissionStatus.GRANTED;
    // this._permissionsStatus[MediaDevices.CAMERA] = videoStatus;
    // this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
    //   mediaDevice: MediaDevices.CAMERA,
    //   status: videoStatus
    // });
    const speakersStatus = this._micDevices.length === 0 ? PermissionStatus.PROMPT : PermissionStatus.GRANTED;
    this._permissionsStatus[MediaDevices.SPEAKERS] = speakersStatus;
    this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
      mediaDevice: MediaDevices.SPEAKERS,
      status: speakersStatus
    });
  }

  async fetchMediaDevices() {
    return new Promise(resolve => {
      navigator.mediaDevices.enumerateDevices().then(mediaDevices => {
        mediaDevices = mediaDevices.filter(d => d.label !== "");
        this._micDevices = mediaDevices
          .filter(d => d.kind === "audioinput")
          .map(d => ({ value: d.deviceId, label: d.label || `Mic Device (${d.deviceId.substring(0, 9)})` }));
        this._videoDevices = mediaDevices
          .filter(d => d.deviceId !== "default" && d.kind === "videoinput")
          .map(d => ({ value: d.deviceId, label: d.label || `Camera Device (${d.deviceId.substring(0, 9)})` }));
        this.selectPreferredVideoDeviceIfAvailable();
        if (MediaDevicesManager.isAudioOutputSelectEnabled) {
          this._outputDevices = mediaDevices
            .filter(d => d.kind === "audiooutput")
            .map(d => ({ value: d.deviceId, label: d.label || `Audio Output (${d.deviceId.substring(0, 9)})` }));
        }
        if (this._outputDevices.length && !this._outputDevices.some(device => device.value === "default")) {
          this._outputDevices.unshift({ value: "default", label: "System default" });
        }
        this.updatePermissions();
        resolve();
      });
    });
  }

  async fetchVideoDevices() {
    return new Promise(resolve => {
      navigator.mediaDevices.enumerateDevices().then(mediaDevices => {
        mediaDevices = mediaDevices.filter(d => d.label !== "");
        this._videoDevices = mediaDevices
          .filter(d => d.deviceId !== "default" && d.kind === "videoinput")
          .map(d => ({ value: d.deviceId, label: d.label || `Camera Device (${d.deviceId.substring(0, 9)})` }));
        this.selectPreferredVideoDeviceIfAvailable();
        // const videoStatus = this._videoDevices.length === 0 ? PermissionStatus.PROMPT : PermissionStatus.GRANTED;
        // this._permissionsStatus[MediaDevices.CAMERA] = videoStatus;
        // if (emit) {
        //   this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
        //     mediaDevice: MediaDevices.CAMERA,
        //     status: videoStatus
        //   });
        // }
        resolve();
      });
    });
  }

  get isAudioOutputSwitchPending() {
    return this._pendingAudioOutputDeviceId !== undefined;
  }

  async changeAudioOutput(deviceId) {
    const request = (this._audioOutputRequest || 0) + 1;
    this._audioOutputRequest = request;
    this._pendingAudioOutputDeviceId = deviceId;
    try {
      const applied = await this.audioSystem.setAudioOutput(deviceId, this.defaultOutputDeviceId);
      if (!applied || request !== this._audioOutputRequest) return false;
      this._pendingAudioOutputDeviceId = undefined;
      this._store.update({ preferences: { preferredSpeakers: deviceId } });
      this.emit(MediaDevicesEvents.AUDIO_OUTPUT_CHANGED);
      return true;
    } catch (error) {
      if (request !== this._audioOutputRequest) return false;
      this._pendingAudioOutputDeviceId = undefined;
      // Older preference views and cross-tab storage events may have already saved
      // the request. Restore their value to the route which is still playing.
      const effectiveDeviceId = this.audioSystem.outputMediaAudio?.sinkId || this.defaultOutputDeviceId;
      if (this._store.state.preferences.preferredSpeakers !== effectiveDeviceId) {
        this._store.update({ preferences: { preferredSpeakers: effectiveDeviceId } });
      }
      this.emit(MediaDevicesEvents.AUDIO_OUTPUT_CHANGED);
      window.notificationsCenter?.info?.(
        "Could not change speakers. Your previous audio output is still selected.",
        null,
        { timeout: 3000 }
      );
      console.warn("Audio output switch failed", error.name);
      return false;
    } finally {
      if (request === this._audioOutputRequest) this._pendingAudioOutputDeviceId = undefined;
    }
  }

  preloadMicNoiseSuppressor() {
    const preferences = this._store.state.preferences || {};
    const AudioContextConstructor =
      typeof window !== "undefined" ? window.AudioContext || window.webkitAudioContext : undefined;

    if (preferences.disableNoiseSuppression === true || !AudioContextConstructor) {
      return Promise.resolve(false);
    }

    if (!this._micNoiseSuppressorPreload) {
      this._micNoiseSuppressorPreload = preloadMicNoiseSuppressorAssets({
        micProcessingPreferences: preferences
      }).catch(error => {
        console.warn("Unable to preload microphone noise suppression assets.", error);
        this._micNoiseSuppressorPreload = null;
        return false;
      });
    }

    return this._micNoiseSuppressorPreload;
  }

  getCameraShareStream() {
    return navigator.mediaDevices.getUserMedia({
      video: this.selectedCameraVideoConstraints
      //TODO: Capture audio from camera?
    });
  }

  addVideoTrackEndedListener(track) {
    track.addEventListener("ended", async () => {
      this._scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source: track["_hubs_contentHint"] });
    });
  }

  emitLocalVideoStreamUpdated(source) {
    const clientId = (typeof NAF !== "undefined" && NAF.clientId) || APP.dialog?._clientId;

    if (clientId) {
      APP.dialog?.emit?.("stream_updated", clientId, "video", source);
    }
  }

  async changeVideoDevice(deviceId) {
    const previousDeviceId = this._videoActiveId;
    this.setDeviceId(deviceId);

    const cameraTracks =
      this._mediaStream?.getVideoTracks?.().filter(track => track["_hubs_contentHint"] === MediaDevices.CAMERA) || [];

    if (cameraTracks.length === 0) {
      this.updatePreferredVideoDevice(deviceId);
      return true;
    }

    let newStream;

    try {
      newStream = await this.getCameraShareStream();
    } catch (e) {
      console.error("Error switching camera device: ", e);
      this.setDeviceId(previousDeviceId);
      return false;
    }

    const newCameraTracks = newStream?.getVideoTracks?.() || [];

    if (newCameraTracks.length === 0) {
      newStream?.getTracks?.().forEach(track => track.stop());
      this.setDeviceId(previousDeviceId);
      return false;
    }

    const stoppedTracks = new Set();
    const stopTrack = track => {
      if (!track || stoppedTracks.has(track)) return;
      track.stop();
      stoppedTracks.add(track);
    };

    cameraTracks.forEach(track => {
      stopTrack(track);
      this._mediaStream.removeTrack(track);
    });

    for (const mediaStream of Array.from(this._videoShareStreams)) {
      const shouldStopStream = mediaStream?.getVideoTracks?.().some(track => cameraTracks.includes(track));

      if (shouldStopStream) {
        mediaStream?.getTracks?.().forEach(stopTrack);
        this._videoShareStreams.delete(mediaStream);
      }
    }

    newCameraTracks.forEach(track => {
      track["_hubs_contentHint"] = MediaDevices.CAMERA;
      this.addVideoTrackEndedListener(track);
      this._mediaStream.addTrack(track);
    });

    this._videoShareStreams.add(newStream);

    await APP.dialog.setLocalMediaStream(this._mediaStream);
    this.emitLocalVideoStreamUpdated(MediaDevices.CAMERA);
    this.updatePreferredVideoDevice(deviceId);

    return true;
  }

  async startMicShare({ deviceId, unmute, updatePrefs = true }) {
    if (this.isMicShared && this.selectedMicDeviceId === deviceId && !this.isMicPreparedLocalOnly) return;

    if (!this.canStartMicShare({ notify: true })) {
      return false;
    }

    if (!deviceId) {
      deviceId = this.preferredOrDefaultMicDeviceId();
    }

    if (this.getPermissionsStatus(MediaDevices.MICROPHONE) === PermissionStatus.DENIED) {
      console.error("Cannot start microphone, permission denied.");
      return false;
    }

    const constraints = this._micConstraintsForDeviceId(deviceId);
    await this._awaitMatchingMutedMicPrewarm(constraints);

    let result = await this._startMicShare(constraints);
    let lastError = result.error;

    // If failed with exact deviceId constraint, try fallback strategies
    if (!result.success && deviceId && deviceId !== NO_DEVICE_ID) {
      const isDeviceIssue =
        lastError &&
        (lastError.name === "OverconstrainedError" ||
          lastError.name === "NotFoundError" ||
          lastError.name === "DevicesNotFoundError");

      if (isDeviceIssue) {
        console.warn(`Failed to start microphone with exact deviceId: ${deviceId}. Attempting fallback...`);

        // Clear the invalid stored preference
        if (updatePrefs) {
          this._store.update({
            preferences: {
              preferredMic: NO_DEVICE_ID
            }
          });
        }

        // Try with generic audio constraint to get any available microphone
        result = await this._startMicShare({ audio: {} });
        lastError = result.error;
      }
    }

    await this.fetchMediaDevices();

    // we should definitely have an audioTrack at this point unless they denied mic access
    if (this.audioTrack) {
      const micDeviceId = deviceId === "default" ? "default" : this.selectedMicDeviceId;
      if (micDeviceId) {
        if (updatePrefs) {
          this._store.update({
            preferences: {
              preferredMic: micDeviceId
            }
          });
        }
      }
    }

    await APP.dialog.setLocalMediaStream(this._mediaStream);

    if (unmute) {
      this.micEnabled = true;
    }

    if (result.success) {
      this._permissionsStatus[MediaDevices.MICROPHONE] = PermissionStatus.GRANTED;
      this._scene.emit(MediaDevicesEvents.MIC_SHARE_STARTED);
      this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
        mediaDevice: MediaDevices.MICROPHONE,
        status: PermissionStatus.GRANTED
      });
    } else {
      // Only set DENIED status for actual permission denials
      const isPermissionDenied =
        lastError && (lastError.name === "NotAllowedError" || lastError.name === "PermissionDeniedError");

      if (isPermissionDenied) {
        this._permissionsStatus[MediaDevices.MICROPHONE] = PermissionStatus.DENIED;
        this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
          mediaDevice: MediaDevices.MICROPHONE,
          status: PermissionStatus.DENIED
        });
      }

      this._scene.emit(MediaDevicesEvents.MIC_SHARE_ENDED);
    }

    return result.success;
  }

  async prepareMutedMicShare({ deviceId, updatePrefs = true } = {}) {
    if (!deviceId) {
      deviceId = this.preferredOrDefaultMicDeviceId();
    }

    const constraints = this._micConstraintsForDeviceId(deviceId);
    const prewarmKey = this._getMicAudioProcessingConfigKey(constraints.audio);

    if (this._pendingMutedMicPrewarm?.key === prewarmKey) {
      return this._pendingMutedMicPrewarm.promise;
    }

    const promise = this._prepareMutedMicShare({ deviceId, constraints, updatePrefs }).finally(() => {
      if (this._pendingMutedMicPrewarm?.promise === promise) {
        this._pendingMutedMicPrewarm = null;
      }
    });

    this._pendingMutedMicPrewarm = { key: prewarmKey, promise };
    return promise;
  }

  async _prepareMutedMicShare({ deviceId, constraints, updatePrefs = true }) {
    if (
      this.getPermissionsStatus(MediaDevices.MICROPHONE) !== PermissionStatus.GRANTED &&
      !this._hasGenericMicPermission
    ) {
      return false;
    }

    if (this.isWaitingForRoomResourceState(RoomResourceIds.MICROPHONE) || !this.canStartMicShare({ notify: false })) {
      return false;
    }

    if (this.getPermissionsStatus(MediaDevices.MICROPHONE) === PermissionStatus.DENIED) {
      return false;
    }

    let result = await this._startMicShare(constraints, {
      attachToOutboundAudio: false,
      restartOnEnded: false
    });
    let lastError = result.error;

    if (!result.success && deviceId && deviceId !== NO_DEVICE_ID) {
      const isDeviceIssue =
        lastError &&
        (lastError.name === "OverconstrainedError" ||
          lastError.name === "NotFoundError" ||
          lastError.name === "DevicesNotFoundError");

      if (isDeviceIssue) {
        console.warn(`Failed to prewarm microphone with exact deviceId: ${deviceId}. Attempting fallback...`);

        if (updatePrefs) {
          this._store.update({
            preferences: {
              preferredMic: NO_DEVICE_ID
            }
          });
        }

        result = await this._startMicShare(
          { audio: {} },
          {
            attachToOutboundAudio: false,
            restartOnEnded: false
          }
        );
        lastError = result.error;
      }
    }

    await this.fetchMediaDevices();

    if (this.audioTrack) {
      const micDeviceId = deviceId === "default" ? "default" : this.selectedMicDeviceId;
      if (micDeviceId && updatePrefs) {
        this._store.update({
          preferences: {
            preferredMic: micDeviceId
          }
        });
      }
    }

    if (result.success) {
      this._permissionsStatus[MediaDevices.MICROPHONE] = PermissionStatus.GRANTED;
      this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
        mediaDevice: MediaDevices.MICROPHONE,
        status: PermissionStatus.GRANTED
      });
      return true;
    }

    const isPermissionDenied =
      lastError && (lastError.name === "NotAllowedError" || lastError.name === "PermissionDeniedError");

    if (isPermissionDenied) {
      this._permissionsStatus[MediaDevices.MICROPHONE] = PermissionStatus.DENIED;
      this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, {
        mediaDevice: MediaDevices.MICROPHONE,
        status: PermissionStatus.DENIED
      });
    }

    return false;
  }

  async _awaitMatchingMutedMicPrewarm(constraints) {
    const pending = this._pendingMutedMicPrewarm;

    if (!pending || pending.key !== this._getMicAudioProcessingConfigKey(constraints.audio)) {
      return false;
    }

    return pending.promise.catch(() => false);
  }

  _micConstraintsForDeviceId(deviceId) {
    if (deviceId && deviceId !== NO_DEVICE_ID) {
      return { audio: { deviceId: { exact: deviceId } } };
    }

    return { audio: {} };
  }

  async _startMicShare(constraints = { audio: {} }, { attachToOutboundAudio = true, restartOnEnded = true } = {}) {
    const micAudioProcessingConfigKey = this._getMicAudioProcessingConfigKey(constraints.audio);

    if (this._canReuseMicAudioProcessing(micAudioProcessingConfigKey)) {
      this._attachMicAudioProcessing(this._micAudioProcessing, micAudioProcessingConfigKey, { attachToOutboundAudio });
      this._micRequestedDeviceId = constraints.audio?.deviceId?.exact || null;
      this._attachMicTrackEndedListener(constraints, { restartOnEnded });
      return { success: true, error: null };
    }

    await this._clearMicAudioProcessing();

    try {
      const micAudioProcessing = await createCleanMicStream({
        audioConstraints: constraints.audio,
        micProcessingPreferences: this._store.state.preferences
      });

      this._attachMicAudioProcessing(micAudioProcessing, micAudioProcessingConfigKey, { attachToOutboundAudio });
      this._micRequestedDeviceId = constraints.audio?.deviceId?.exact || null;
      this._attachMicTrackEndedListener(constraints, { restartOnEnded });

      return { success: true, error: null };
    } catch (e) {
      console.error("Error during getUserMedia: ", e);
      this.audioTrack = null;

      // Differentiate between permission denials and other errors
      const isPermissionDenied = e.name === "NotAllowedError" || e.name === "PermissionDeniedError";
      const isDeviceNotFound = e.name === "NotFoundError" || e.name === "DevicesNotFoundError";
      const isOverconstrained = e.name === "OverconstrainedError";

      if (isPermissionDenied) {
        console.error("Microphone permission denied by user");
      } else if (isDeviceNotFound) {
        console.warn(`No microphone device found: ${e.message}`);
      } else if (isOverconstrained) {
        console.warn(`Microphone constraints could not be satisfied: ${e.message}`);
      } else {
        console.warn(`Microphone access failed: ${e.name} - ${e.message}`);
      }

      return { success: false, error: e };
    }
  }

  _getMicAudioProcessingConfigKey(audioConstraints) {
    return micAudioProcessingConfigKey(audioConstraints, this._store.state.preferences);
  }

  _canReuseMicAudioProcessing(micAudioProcessingConfigKey) {
    return (
      this._micAudioProcessing &&
      this._micAudioProcessingConfigKey === micAudioProcessingConfigKey &&
      hasLiveAudioTrack(this._micAudioProcessing.rawStream) &&
      hasLiveAudioTrack(this._micAudioProcessing.cleanStream)
    );
  }

  _removeMicTrackEndedListener() {
    if (this._micTrackEndedTarget && this._micTrackEndedListener) {
      this._micTrackEndedTarget.removeEventListener?.("ended", this._micTrackEndedListener);
    }

    this._micTrackEndedTarget = null;
    this._micTrackEndedListener = null;
  }

  _attachMicTrackEndedListener(constraints, { restartOnEnded = true } = {}) {
    this._removeMicTrackEndedListener();

    if (!this.audioTrack?.addEventListener) {
      return;
    }

    if (!restartOnEnded) {
      this._micTrackEndedTarget = this.audioTrack;
      this._micTrackEndedListener = async () => {
        this._removeMicTrackEndedListener();
        await this._clearMicAudioProcessing();
      };
      this.audioTrack.addEventListener("ended", this._micTrackEndedListener, { once: true });
      return;
    }

    const isOculusBrowser = /Oculus/.test(navigator.userAgent);

    if (isOculusBrowser) {
      // HACK Oculus Browser 6 seems to randomly end the microphone audio stream. This re-creates it.
      // Note the ended event will only fire if some external event ends the stream, not if we call stop().
      this._micTrackEndedListener = async () => {
        console.warn(
          "Oculus Browser 6 bug hit: Audio stream track ended without calling stop. Recreating audio stream."
        );

        await this._clearMicAudioProcessing();
        const micAudioProcessing = await createCleanMicStream({
          audioConstraints: constraints.audio,
          micProcessingPreferences: this._store.state.preferences
        });

        this._attachMicAudioProcessing(micAudioProcessing, this._getMicAudioProcessingConfigKey(constraints.audio));
        this._micRequestedDeviceId = constraints.audio?.deviceId?.exact || null;
        this._attachMicTrackEndedListener(constraints, { restartOnEnded: true });
        this._scene.emit(MediaDevicesEvents.MIC_SHARE_STARTED);
      };
    } else {
      this._micTrackEndedListener = () => {
        this._removeMicTrackEndedListener();
        this._scene.emit(MediaDevicesEvents.MIC_SHARE_ENDED);
        this.startMicShare({ unmute: this.isMicEnabled });
      };
    }

    this._micTrackEndedTarget = this.audioTrack;
    this.audioTrack.addEventListener("ended", this._micTrackEndedListener, { once: true });
  }

  _attachMicAudioProcessing(micAudioProcessing, micAudioProcessingConfigKey, { attachToOutboundAudio = true } = {}) {
    const alreadyAttached = this._micAudioProcessing === micAudioProcessing && this._micAudioProcessingAttached;

    this._micAudioProcessing = micAudioProcessing;
    this._micAudioProcessingConfigKey = micAudioProcessingConfigKey;
    this._micAudioProcessingLabel = micAudioProcessing.processing;
    if (attachToOutboundAudio && !alreadyAttached) {
      this.audioSystem.addStreamToOutboundAudio("microphone", micAudioProcessing.cleanStream);
      this._micAudioProcessingAttached = true;
    }
    this.audioTrack =
      micAudioProcessing.rawStream.getAudioTracks()[0] || micAudioProcessing.cleanStream.getAudioTracks()[0] || null;
  }

  async _clearMicAudioProcessing() {
    this._removeMicTrackEndedListener();
    this.audioSystem.removeStreamFromOutboundAudio("microphone");
    this._micAudioProcessingAttached = false;

    if (this._micAudioProcessing) {
      await this._micAudioProcessing.cleanup();
      this._micAudioProcessing = null;
    } else if (this.audioTrack) {
      this.audioTrack.stop();
    }

    this.audioTrack = null;
    this._micRequestedDeviceId = null;
    this._micAudioProcessingConfigKey = null;
    this._micAudioProcessingLabel = null;
  }

  async stopMicShare() {
    await this._clearMicAudioProcessing();

    await APP.dialog.setLocalMediaStream(this._mediaStream);
    APP.dialog.enableMicrophone(false);
  }

  async startVideoShare({ isDisplayMedia, target, success, error }) {
    let newStream;
    let videoTrackAdded = false;
    const mediaDevice = isDisplayMedia ? MediaDevices.SCREEN : MediaDevices.CAMERA;
    const unavailableReason = this.videoShareUnavailableReason({ isDisplayMedia });

    if (unavailableReason) {
      this.notifyUnavailableRoomResource(unavailableReason);
      error?.(new Error(unavailableReason), mediaDevice);
      this._scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source: mediaDevice });
      return false;
    }

    try {
      if (isDisplayMedia) {
        newStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            deviceId: this._videoActiveId,
            // Work around BMO 1449832 by calculating the width. This will break for multi monitors if you share anything
            // other than your current monitor that has a different aspect ratio.
            // Have deactivated the above workaround to achieve a better quality for the screen sharing.
            width: screen.width,
            height: screen.height,
            frameRate: 30
          },
          audio: {
            echoCancellation: window.APP.store.state.preferences.disableEchoCancellation === true ? false : true,
            noiseSuppression: window.APP.store.state.preferences.disableNoiseSuppression === true ? false : true,
            autoGainControl: window.APP.store.state.preferences.disableAutoGainControl === true ? false : true
          }
        });
      } else {
        newStream = await this.getCameraShareStream();
      }

      const videoTracks = newStream ? newStream.getVideoTracks() : [];
      if (videoTracks.length > 0) {
        videoTrackAdded = true;

        newStream.getVideoTracks().forEach(track => {
          // Ideally we would use track.contentHint but it seems to be read-only in Chrome so we just add a custom property
          track["_hubs_contentHint"] = isDisplayMedia ? MediaDevices.SCREEN : MediaDevices.CAMERA;
          this.addVideoTrackEndedListener(track);
          this._mediaStream.addTrack(track);
        });

        if (newStream && newStream.getAudioTracks().length > 0) {
          this.audioSystem.addStreamToOutboundAudio("screenshare", newStream);
        }

        this._videoShareStreams.add(newStream);

        await APP.dialog.setLocalMediaStream(this._mediaStream);

        this._permissionsStatus[mediaDevice] = PermissionStatus.GRANTED;
        this._scene.emit(MediaDevicesEvents.VIDEO_SHARE_STARTED);
        this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, { mediaDevice, status: PermissionStatus.GRANTED });
      }
    } catch (e) {
      error(e, mediaDevice);
      this._permissionsStatus[mediaDevice] = PermissionStatus.DENIED;
      this._scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source: mediaDevice });
      this.emit(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, { mediaDevice, status: PermissionStatus.DENIED });
      return false;
    }

    success(isDisplayMedia, videoTrackAdded, target);
    return true;
  }

  async stopVideoShare({ source } = {}) {
    if (!this._mediaStream) return;

    const stoppedTracks = new Set();
    const stopTrack = track => {
      if (!track || stoppedTracks.has(track)) return;
      track.stop();
      stoppedTracks.add(track);
    };

    for (const track of this._mediaStream.getVideoTracks()) {
      if (source && track["_hubs_contentHint"] !== source) {
        continue;
      }

      stopTrack(track); // Stop video track to remove the "Stop screen sharing" bar right away.
      this._mediaStream.removeTrack(track);
    }

    for (const mediaStream of Array.from(this._videoShareStreams)) {
      const videoTracks = mediaStream?.getVideoTracks?.() || [];
      const shouldStopStream = !source || videoTracks.some(track => track["_hubs_contentHint"] === source);

      if (!shouldStopStream) {
        continue;
      }

      mediaStream?.getTracks?.().forEach(stopTrack);
      this._videoShareStreams.delete(mediaStream);
    }

    if (!source || source === MediaDevices.SCREEN) {
      this.audioSystem.removeStreamFromOutboundAudio("screenshare");
    }

    await APP.dialog.setLocalMediaStream(this._mediaStream);
  }

  async shouldShowHmdMicWarning() {
    if (isMobile || AFRAME.utils.device.isMobileVR()) return false;
    if (!this.state.enterInVR) return false;
    if (!this.hasHmdMicrophone()) return false;

    return !HMD_MIC_REGEXES.find(r => this.selectedMicLabel.match(r));
  }

  micLabelForAudioTrack(audioTrack) {
    const label = (audioTrack && audioTrack.label) || "";
    if (label.indexOf("Default - ") < 0) {
      return label;
    } else {
      return label.substring(10);
    }
  }

  deviceIdForMicDeviceLabel(label) {
    return this._micDevices.filter(d => d.label === label).map(d => d.value)[0] || this.defaultInputDeviceId;
  }

  deviceIdForSpeakersDeviceLabel(label) {
    return this._outputDevices.filter(d => d.label === label).map(d => d.value)[0] || this.defaultOutputDeviceId;
  }

  micLabelForDeviceId(deviceId) {
    return this._micDevices.filter(d => d.value === deviceId).map(d => d.label)[0];
  }

  speakersLabelForDeviceId(deviceId) {
    return this._outputDevices.filter(d => d.value === deviceId).map(d => d.label)[0];
  }

  hasHmdMicrophone() {
    return !!this.state._micDevices.find(d => HMD_MIC_REGEXES.find(r => d.label.match(r)));
  }

  videoDeviceIdForMicLabel(label) {
    return this._videoDevices.filter(d => d.label === label).map(d => d.value)[0];
  }

  videoLabelForDeviceId(deviceId) {
    return this._videoDevices.filter(d => d.value === deviceId).map(d => d.label)[0];
  }
}
